/**
 * PricingSourcePicker - Per-line-item pricing source badge & dropdown.
 *
 * Shows WHERE the current price came from (Special Pricing, Quote, seasonal
 * Promotion, agreement base discount, Standard, or Custom) and, when `allowPick` is set, lets the user pick a different
 * source. Used by the draft editor (OrderDetailModal, gated on draft status)
 * and by the Edit-in-Sage modal (always pickable — the pick is staged and
 * pushed to Sage with the rest of the edit).
 *
 * All source detection is done server-side — the `pricingItem` prop carries
 * backend-classified `current_source` (what the ordered price matches) and
 * `available_sources` (every option for the picker).
 */

import { useState, useRef, useCallback } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import type {
  DetectedPricingSource,
  LineItemPricingContext,
  PricingSourceOption,
  PricingSourceType,
} from '../../services/portalOrders';
import styles from './PricingSourcePicker.module.css';

// Re-export so downstream callers continue to import from this module.
export type { PricingSourceType, PricingSourceOption as PricingOption };

interface PricingSourcePickerProps {
  /** Item code of the line the picker sits on (dropdown title only). */
  itemCode: string;
  pricingItem: LineItemPricingContext | undefined;
  /** Enable the dropdown of alternative sources (badge is always shown). */
  allowPick: boolean;
  onApplyPrice: (price: number) => void;
  formatPrice: (val: string | number | null | undefined) => string;
}

// ── Badge rendering helpers ──

const NONE_SOURCE: DetectedPricingSource = {
  type: 'none',
  quote_source_id: null,
  tier_label: null,
  tier_price: null,
};

function getSourceBadgeLabel(source: DetectedPricingSource): string {
  switch (source.type) {
    case 'special': return 'SP';
    case 'tiered': return 'SP';
    case 'quote': return `Qt #${source.quote_source_id ?? '?'}`;
    case 'base_discount':
      return source.base_discount_pct != null ? `SP −${source.base_discount_pct}%` : 'SP';
    case 'promotion': return 'Promo';
    case 'standard': return 'Std';
    case 'custom': return 'Custom';
    case 'none': return '—';
  }
}

function getSourceBadgeClass(type: PricingSourceType): string {
  switch (type) {
    case 'special':
    case 'tiered':
    case 'base_discount':
      return styles.badgeSP;
    case 'quote':
      return styles.badgeQuote;
    case 'promotion':
      return styles.badgePromo;
    case 'standard':
      return styles.badgeStandard;
    case 'custom':
      return styles.badgeCustom;
    case 'none':
      return styles.badgeNone;
  }
}

function getSourceTooltip(
  source: DetectedPricingSource,
  formatPrice: (val: string | number | null | undefined) => string,
): string {
  switch (source.type) {
    case 'tiered':
      if (source.tier_label && source.tier_price !== null) {
        return `Special Pricing — ${source.tier_label}: ${formatPrice(source.tier_price)}`;
      }
      return 'Special Pricing (tiered)';
    case 'special':
      return 'Special Pricing (flat rate)';
    case 'quote':
      return `Quote #${source.quote_source_id ?? '?'}`;
    case 'base_discount': {
      const pct = source.base_discount_pct != null ? ` −${source.base_discount_pct}%` : '';
      const list = source.list_price != null ? ` off ${formatPrice(source.list_price)} list` : '';
      return `Special Pricing — base discount${pct}${list}`;
    }
    case 'promotion':
      return source.promotion_label
        ? `Promotion — ${source.promotion_label}${
            source.promotion_price != null ? `: ${formatPrice(source.promotion_price)}` : ''
          }`
        : 'Seasonal promotion';
    case 'standard':
      return 'Standard catalog price';
    case 'custom':
      return 'Custom price (not from any pricing source)';
    case 'none':
      return 'No price set';
  }
}

function optionBadgeText(type: PricingSourceOption['type']): string {
  switch (type) {
    case 'special':
    case 'tiered':
    case 'base_discount':
      return 'SP';
    case 'quote':
      return 'Qt';
    case 'promotion':
      return 'Promo';
    case 'standard':
      return 'Std';
  }
}

// ── Component ──

export default function PricingSourcePicker({
  itemCode,
  pricingItem,
  allowPick,
  onApplyPrice,
  formatPrice,
}: PricingSourcePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const closeDropdown = useCallback(() => setIsOpen(false), []);
  useClickOutside(wrapperRef, closeDropdown, isOpen);

  const currentSource: DetectedPricingSource =
    pricingItem?.current_source ?? NONE_SOURCE;
  const options: PricingSourceOption[] = pricingItem?.available_sources ?? [];
  const badgeLabel = getSourceBadgeLabel(currentSource);
  const badgeClass = getSourceBadgeClass(currentSource.type);
  const hasOptions = options.length > 0 && allowPick;

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={`${styles.badge} ${badgeClass} ${hasOptions ? styles.badgeClickable : ''}`}
        data-action="line_price_source_open"
        data-target-item-code={itemCode}
        aria-expanded={hasOptions ? isOpen : undefined}
        onClick={() => {
          if (hasOptions) setIsOpen(!isOpen);
        }}
        title={hasOptions ? 'Click to see pricing options' : getSourceTooltip(currentSource, formatPrice)}
      >
        {badgeLabel}
        {hasOptions && (
          <svg className={styles.chevron} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <polyline points={isOpen ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
          </svg>
        )}
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownTitle}>Pricing Sources for {itemCode}</div>
          {options.length === 0 ? (
            <div className={styles.dropdownEmpty}>No pricing sources available</div>
          ) : (
            options.map((opt, i) => (
              <button
                key={`${opt.type}-${opt.quote_source_id ?? i}`}
                type="button"
                className={`${styles.option} ${opt.is_active ? styles.optionActive : ''}`}
                data-action="line_price_source_pick"
                data-record-value="price_source"
                value={opt.type}
                data-target-item-code={itemCode}
                onClick={() => {
                  onApplyPrice(opt.price);
                  setIsOpen(false);
                }}
                disabled={opt.is_active}
              >
                <div className={styles.optionLeft}>
                  <span className={`${styles.optionBadge} ${getSourceBadgeClass(opt.type)}`}>
                    {optionBadgeText(opt.type)}
                  </span>
                  <div className={styles.optionLabels}>
                    <span className={styles.optionLabel}>{opt.label}</span>
                    {opt.sublabel && <span className={styles.optionSublabel}>{opt.sublabel}</span>}
                  </div>
                </div>
                <div className={styles.optionRight}>
                  <span className={styles.optionPrice}>{formatPrice(opt.price)}</span>
                  {opt.is_active && <span className={styles.activeMark}>current</span>}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
